const { formatMoney, getElements, getElement } = window.BAO.Utils

/**
 * @typedef {Object} SelectedVariantOption
 *
 * @property {string} label
 * @property {string} value
 */

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStockTrigger: '[data-product-bis-trigger]',
  stockIndicator: '[data-quick-buy-product-stock]',
  limitedStock: '[data-product-limited-stock]',
  shadeVariantTitle: '[data-variant-shade-title]',
  shadeVariantURL: '[data-variant-url]',
  productImageThumbnail: '[data-product-image-thumbnail]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  ProductUnitMeasurement: '[data-variant-unit-measurement]',
  ProductUnitMeasurementText: '[data-variant-unit-measurement-text]',
  sku: '[data-product-sku]',
  multipack: '[data-product-multipack]',
  multipackVariants: '[data-multipack-variants]',
  multipackInput: '[data-multipack-item-properties]',
}

class VariantQuickBuy {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */

    this.data = data
    this.variantData = JSON.parse(document.getElementById('variant-quick-buy-data').textContent)

    // Used for section rendering API
    this.sectionId = `${document.body.getAttribute('data-template-name')}-${document.body.getAttribute('data-template-suffix')}`
  }

  setPrice () {
    this.unhidePriceEls()

    const priceElements = getElements(selectors.price)
    priceElements.forEach(el => (el.innerHTML = formatMoney(this.data.price)))

    const priceCompareElements = getElements(selectors.priceCompare)
    priceCompareElements.forEach(
      el => (el.innerHTML = formatMoney(this.data.compare_at_price)),
    )

    if (!this.data.compare_at_price) {
      this.hidePriceCompareElements()
    }
  }

  setUnitMeasurement () {
    const unitMeasurementElement = getElement(selectors.ProductUnitMeasurement)

    // If there's no unit measurement element, exit early
    if (!unitMeasurementElement) return

    // Find the selected variant's data from the JSON object
    const variant = this.variantData.variants.find(v => v.id === this.data.id)

    // If the variant exists, swap the price per unit HTML
    if (variant && variant.price_per_unit) {
      unitMeasurementElement.innerHTML = variant.price_per_unit
      unitMeasurementElement.removeAttribute('hidden')
    } else {
      // Clear the content and hide the parent element if no valid variant
      unitMeasurementElement.innerHTML = ''
      unitMeasurementElement.setAttribute('hidden', 'true')
    }
  }

  setAvailability () {
    if (!this.variantData || !this.variantData.variants) return

    getElements(selectors.addToCart).forEach(
      el => {
        el.disabled = !this.variantData.variants.find(v => v.id === this.data.id).available
        if (!el.hasAttribute('data-no-hide')) {
          el.setAttribute('aria-hidden', !this.variantData.variants.find(v => v.id === this.data.id).available)
        }
      },
    )

    getElements(`${selectors.backInStockTrigger}`).forEach(el => {
      el.setAttribute('aria-hidden', this.variantData.variants.find(v => v.id === this.data.id).available)
      el.setAttribute(
        'data-product-url',
        `${this.url.toString()}?variant=${this.data.id}`,
      )
    })

    getElements(`${selectors.stockIndicator}`).forEach(el => {
      if (this.variantData.variants.find(v => v.id === this.data.id).available === true) {
        el.parentElement.querySelector('.prd-QuickBuyDetail_StockInfo-instock').setAttribute('aria-hidden', 'false')
        el.parentElement.querySelector('.prd-QuickBuyDetail_StockInfo-outofstock').setAttribute('aria-hidden', 'true')
      } else {
        el.parentElement.querySelector('.prd-QuickBuyDetail_StockInfo-instock').setAttribute('aria-hidden', 'true')
        el.parentElement.querySelector('.prd-QuickBuyDetail_StockInfo-outofstock').setAttribute('aria-hidden', 'false')
      }
    })

    getElements(selectors.addToCartText).forEach(
      el =>
        (el.textContent = this.variantData.variants.find(v => v.id === this.data.id).available
          ? window.theme.strings.addToCart
          : window.theme.strings.soldOut),
    )
  }

  async setLimitedStock () {
    // Get the template suffix if not using the default product template.
    const templateSuffix = document.body.getAttribute('data-template-suffix')

    let productUrl = this.url

    if (templateSuffix) {
      productUrl = new URL(this.url.toString())
      productUrl.searchParams.set('view', templateSuffix)
      productUrl = productUrl.toString()
    }

    const fullSectionId = getElement('[data-product-section-wrapper]')?.getAttribute('data-section-id')
    if (!fullSectionId) return

    const html = await window.BAO.sectionRenderer.fetchSingle(fullSectionId, productUrl)
    if (!html) return

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const limitedStock = doc.querySelector(selectors.limitedStock)
    if (!limitedStock) return

    const limitedStockEl = getElement(selectors.limitedStock)
    if (!limitedStockEl) return

    limitedStockEl.innerHTML = limitedStock.innerHTML
  }

  updateInput () {
    const inputs = getElements('[data-quickbuy-form] [name="id"]')

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  updateNotifyMeAnchorUrl (event) {
    const notifyMeBody = event.target.closest('.drw-QuickBuy_Body')

    if (!notifyMeBody) return

    const notifyMeButton = notifyMeBody.querySelector('[data-product-bis-trigger]')

    if (!notifyMeButton) return

    const variantUrl = `${event.target.dataset.variantUrl}?&=open-modal`
    notifyMeButton.setAttribute('href', variantUrl)
  }

  updateCustomSoldOutText (event) {
    const clickedElement = event.target

    if (clickedElement.type === 'radio') {
      const specificElement = clickedElement.closest('.prd-DetailVariants_Option-radio')?.querySelector('.prd-DetailVariants_Button')
      if (!specificElement) return
      const soldOutText = specificElement.getAttribute('data-custom-sold-out-text')

      if (soldOutText != null) {
        const addToCartButton = clickedElement.closest('.prd-ShadeFinder_Form').querySelector('.prd-DetailForm_Button-bis')
        const addToCartButtonText = addToCartButton.querySelectorAll('.prd-DetailForm_ButtonText')

        addToCartButtonText.forEach(buttonText => {
          buttonText.innerHTML = soldOutText
        })
      }
    }
  }

  // move into own section

  updateEthnicityMedia (event) {
    const clickedElement = event.target
    let pageEthnicityShadeMediaSrc
    let pageEthnicityShadeMediaBeforeSrc
    let pageEthnicityShadeMediaAfterSrc

    const specificElement = clickedElement.closest('.prd-DetailVariants_Option-radio')?.querySelector('.prd-DetailVariants_Button')
    if (!specificElement) return
    const ethnicityGroupMainVideo = specificElement.getAttribute('data-shade-ethnicity-group-video')
    const ethnicityGroupMainImg = specificElement.getAttribute('data-shade-ethnicity-group-img')
    const ethnicityGroupBeforeImg = specificElement.getAttribute('data-shade-ethnicity-group-before-after-img_before')
    const ethnicityGroupAfterImg = specificElement.getAttribute('data-shade-ethnicity-group-before-after-img_after')

    if (ethnicityGroupMainImg) {
      pageEthnicityShadeMediaSrc = ethnicityGroupMainImg

      const radioValue = clickedElement.value
      // const selectElements = document.querySelectorAll('select[data-quick-buy-variant-selects-el="option-choice"]')
      const selectElements = clickedElement.closest('.prd-DetailVariants_Row').querySelectorAll('select[data-quick-buy-variant-selects-el="option-choice"]')

      selectElements.forEach(selectElement => {
        selectElement.value = radioValue

        const changeEvent = new Event('change', { bubbles: true })
        selectElement.dispatchEvent(changeEvent)
      })

      // Create values for Main Image
      const pageEthnicityMediaImageSrc = pageEthnicityShadeMediaSrc
      const pageEthnicityMediaImageSrc180 = pageEthnicityShadeMediaSrc.replace('&width=800', '&width=180')
      const pageEthnicityMediaImageSrc360 = pageEthnicityShadeMediaSrc.replace('&width=800', '&width=360')
      const pageEthnicityMediaImageSrc540 = pageEthnicityShadeMediaSrc.replace('&width=800', '&width=540')
      const pageEthnicityMediaImageSrc720 = pageEthnicityShadeMediaSrc.replace('&width=800', '&width=720')

      // Loop through all 'data-ethnicity-image-respective' elements
      const ethnicityImageElements = getElements('[data-ethnicity-image-respective]')
      ethnicityImageElements.forEach(element => {
        // Find closest section media video
        const ethnicityMainVideoElement = element.closest('.sec-TabContent_Body').querySelector('.sec-TabContent_Video video')

        // Replace previous Main video source src with new one
        if (ethnicityMainVideoElement) {
          // Create a new image element
          const pageEthnicityMainImage = `
              <div class="sec-TabContent_Image" data-ethnicity-image-respective>
                <img src="${pageEthnicityMediaImageSrc}" srcset="${pageEthnicityMediaImageSrc180} 180w, ${pageEthnicityMediaImageSrc360} 360w, ${pageEthnicityMediaImageSrc540} 540w, ${pageEthnicityMediaImageSrc720} 720w" width="800" height="800" loading="lazy" class="rsp-Image_Image" sizes="(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 3)">
              </div>
            `

          // Find the parent element to insert the video element
          const parentElement = element.closest('.sec-TabContent_BodyWrap')

          parentElement.innerHTML += pageEthnicityMainImage

          const videoEl = parentElement.querySelector('.sec-TabContent_Video')
          videoEl.remove()
        } else {
          // Find closest section media image
          const ethnicityMainImageElement = element.closest('.sec-TabContent_Body').querySelector('.sec-TabContent_Image')

          // Replace previous Main image with new one
          if (element) {
            const pageEthnicityImage = `
                <img src="${pageEthnicityMediaImageSrc}" srcset="${pageEthnicityMediaImageSrc180} 180w, ${pageEthnicityMediaImageSrc360} 360w, ${pageEthnicityMediaImageSrc540} 540w, ${pageEthnicityMediaImageSrc720} 720w" width="800" height="800" loading="lazy" class="rsp-Image_Image" sizes="(max-width: 767px) 100vw, (min-width: 768px) calc(100vw / 3)">
                `
            ethnicityMainImageElement.innerHTML = pageEthnicityImage
          }
        }
      })
    }
    if (ethnicityGroupMainVideo) {
      pageEthnicityShadeMediaSrc = ethnicityGroupMainVideo

      const radioValue = clickedElement.value
      // const selectElements = document.querySelectorAll('select[data-quick-buy-variant-selects-el="option-choice"]')
      const selectElements = clickedElement.closest('.prd-DetailVariants_Row').querySelectorAll('select[data-quick-buy-variant-selects-el="option-choice"]')

      selectElements.forEach(selectElement => {
        selectElement.value = radioValue

        const changeEvent = new Event('change', { bubbles: true })
        selectElement.dispatchEvent(changeEvent)
      })

      // Create values for Main Video
      const pageEthnicityMediaVideoSrc = pageEthnicityShadeMediaSrc

      // Loop through all 'data-ethnicity-image-respective' elements
      const ethnicityRespectiveElements = getElements('[data-ethnicity-image-respective]')
      ethnicityRespectiveElements.forEach(element => {
        // Find closest section media video
        const ethnicityMainVideoElement = element.closest('.sec-TabContent_Body').querySelector('.sec-TabContent_Video video')

        // Find closest section media image
        const ethnicityMainImageElement = element.closest('.sec-TabContent_Body').querySelector('.sec-TabContent_Image')

        if (ethnicityMainVideoElement) {
          // Replace previous Video source src with new one
          ethnicityMainVideoElement.src = pageEthnicityMediaVideoSrc
        } else if (ethnicityMainImageElement) {
          // Create a new video element
          const ethnicityMainVideo = document.createElement('video')
          ethnicityMainVideo.classList.add('sec-TabContent_Video')
          ethnicityMainVideo.src = pageEthnicityMediaVideoSrc

          const pageEthnicityMainVideo = `
              <div class="sec-TabContent_Video" data-ethnicity-image-respective>
                <video
                  playsinline="playsinline"
                  autoplay="autoplay"
                  loop="loop"
                  muted="muted"
                  preload="metadata"
                >
                    <source src="${pageEthnicityMediaVideoSrc}" type="video/mp4">
                </video>
              </div>
            `

          // Find the parent element to insert the video element
          const parentElement = element.closest('.sec-TabContent_BodyWrap')

          parentElement.querySelector('.sec-TabContent_BodyMain').insertAdjacentHTML('afterend', pageEthnicityMainVideo)

          const imageEl = parentElement.querySelector('.sec-TabContent_Image')

          imageEl.remove()
        }
      })
    }

    if (ethnicityGroupBeforeImg !== 'null' && ethnicityGroupAfterImg !== 'null') {
      pageEthnicityShadeMediaBeforeSrc = ethnicityGroupBeforeImg

      const radioValue = clickedElement.value
      // const selectElements = document.querySelectorAll('select[data-quick-buy-variant-selects-el="option-choice"]')
      const selectElements = clickedElement.closest('.prd-DetailVariants_Row').querySelectorAll('select[data-quick-buy-variant-selects-el="option-choice"]')

      selectElements.forEach(selectElement => {
        selectElement.value = radioValue

        const changeEvent = new Event('change', { bubbles: true })
        selectElement.dispatchEvent(changeEvent)
      })

      pageEthnicityShadeMediaAfterSrc = ethnicityGroupAfterImg

      // Create values for Before Image
      const pageEthnicityBeforeAfterBeforeSrc = pageEthnicityShadeMediaBeforeSrc

      // Create a new image element
      const pageEthnicityBeforeAfterImageBeforeEl = `<img src="${pageEthnicityBeforeAfterBeforeSrc}" width="800" height="800" loading="lazy" style="width: 2000px;">`

      // Loop through all 'data-ethnicity-beforeafter-image-respective_before' elements
      const ethnicityBeforeAfterElementsBefore = getElements('[data-ethnicity-beforeafter-image-respective_before]')
      ethnicityBeforeAfterElementsBefore.forEach(element => {
        // Find closest section 'Before' media image
        const ethnicityBeforeAfterElement = element.closest('.sec-TabContent_Body').querySelector('.sec-BeforeAfter_BeforeShot')

        // Replace previous 'Before' image with new one
        if (element) {
          ethnicityBeforeAfterElement.innerHTML = pageEthnicityBeforeAfterImageBeforeEl
        }
      })

      // Create values for After Image
      const pageEthnicityBeforeAfterAfterSrc = pageEthnicityShadeMediaAfterSrc

      // Create a new image element
      const pageEthnicityBeforeAfterImageAfterEl = `<img src="${pageEthnicityBeforeAfterAfterSrc}" width="800" height="800" loading="lazy">`

      // Loop through all 'data-ethnicity-beforeafter-image-respective_after' elements
      const ethnicityBeforeAfterElementsAfter = getElements('[data-ethnicity-beforeafter-image-respective_after]')
      ethnicityBeforeAfterElementsAfter.forEach(element => {
        // Find closest section 'After' media image
        const ethnicityBeforeAfterAfterElement = element.closest('.sec-TabContent_Body').querySelector('.sec-BeforeAfter_AfterShot')

        // Replace previous 'After' image with new one
        if (element) {
          ethnicityBeforeAfterAfterElement.innerHTML = pageEthnicityBeforeAfterImageAfterEl
        }
      })
    }
  }
  // move into own section

  updateSwatchTitle (event, multipack) {
    const eventTarget = event.target

    // Local fieldset title
    const thisFieldset = eventTarget.closest('[data-fieldset]')
    const variantFeaturedImage = eventTarget.dataset.variantFeaturedImage

    if (!thisFieldset) return

    const fieldsetTitle = thisFieldset.querySelector(`${selectors.shadeVariantTitle}`)
    fieldsetTitle.innerHTML = eventTarget.value

    if (!variantFeaturedImage || multipack) return

    const cartThumbnail = document.querySelector(`${selectors.productImageThumbnail}`)
    const cartThumbnailImage = cartThumbnail.querySelector('img')

    cartThumbnailImage.src = variantFeaturedImage
    cartThumbnailImage.srcset = variantFeaturedImage
    cartThumbnailImage.alt = fieldsetTitle.innerHTML
  }

  updateSwatchDescription (event) {
    if (!event.target.value) return

    const eventTarget = event.target
    const thisFieldset = eventTarget.closest('[data-fieldset]')
    if (!thisFieldset) return

    const currentVariantDescriptions = thisFieldset.querySelectorAll('[data-current-variant-description]')
    const currentVariantTitle = thisFieldset.querySelector('[data-current-variant-title]')
    const currentVariantImage = thisFieldset.querySelector('[data-current-variant-image]')
    const currentBundledImage = thisFieldset.querySelector('[data-bundled-image] img')

    currentVariantDescriptions.forEach(description => {
      description.innerHTML = eventTarget.dataset.variantDescription
    })
    currentVariantTitle.innerHTML = eventTarget.dataset.variantTitle

    if (!currentVariantImage) return

    // prefer a better way but for speed will do for now
    const chosenVariantImage = eventTarget.closest('div')?.querySelector('[data-variant-image]')
    if (!chosenVariantImage) return
    currentVariantImage.innerHTML = chosenVariantImage.outerHTML

    if (!currentBundledImage) return

    currentBundledImage.src = `https:${eventTarget.dataset.variantBundledImage}`
    currentBundledImage.srcset = ''
    currentBundledImage.sizes = ''
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  handleMultipack (event) {
    const eventTarget = event.target
    const thisFieldset = eventTarget.closest('[data-fieldset]')
    if (!thisFieldset) return

    thisFieldset.dataset.multipackVariant = eventTarget.dataset.variantTitle
  }

  hideElements (elements) {
    elements.forEach(el => (el.hidden = true))
  }

  unhideElements (elements) {
    elements.forEach(el => (el.hidden = false))
  }

  unhidePriceEls () {
    this.unhideElements(getElements(`${selectors.price}, ${selectors.priceCompare}`))
  }

  hidePriceCompareElements () {
    this.hideElements(getElements(selectors.priceCompare))
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

export class VariantOptions extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data', 'option', 'option-choice']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new VariantQuickBuy(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    this.onVariantChange = this.onVariantChange.bind(this)

    this.listeners.add(this, 'change', this.onVariantChange)
  }

  onVariantChange (event) {
    const variant = this.getVariant()
    const multipack = this.closest(selectors.multipack) !== null

    if (!variant) {
      this.resetPage()
      return
    }

    this.updateVariant(variant, event, multipack)
  }

  updateVariant (variant, event, multipack) {
    variant.updateSwatchDescription(event)
    variant.updateSwatchTitle(event, multipack)

    if (!multipack) {
      variant.setLimitedStock()
      variant.setAvailability()
      variant.setPrice()
      variant.setUnitMeasurement()
      variant.updateInput()
      variant.updateNotifyMeAnchorUrl(event)
      variant.updateEthnicityMedia(event) // move into own section
      // variant.updateCustomSoldOutText(event) // interesting
      variant.setUrl()
      variant.setShareUrl()
    } else {
      variant.handleMultipack(event)
    }

    document.dispatchEvent(new CustomEvent('variantChange', { detail: { variant } }))
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage () {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new VariantQuickBuy(data)
    this.collectedVariants.push(variant)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    if (!this.currentVariant) {
      return this.collectedVariants[0]
    }

    const existingVariant = this.collectedVariants.find(
      variant => variant.id === this.currentVariant.id,
    )
    if (existingVariant) return existingVariant

    const matchedVariant = this.data.find(variant => {
      return variant.options.every(
        (option, index) => this.selectedOptions[index]?.value === option,
      )
    })

    if (matchedVariant) {
      return this.createVariant(matchedVariant)
    }

    return this.collectedVariants[0]
  }

  /**
   * @returns {SelectedVariantOption[]}
   */
  get selectedOptions () {
    this.throwError('selectedOptions needs to be implemented on this class')

    return []
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this.data.find(variant => {
      return variant.options.every(
        (option, index) => (
          this.selectedOptions[index].value === option
        ),
      )
    })
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

export class QuickBuyVariantRadios extends VariantOptions {
  get selectedOptions () {
    const options = this.els.option.elements.map(el => {
      const { name } = el.dataset
      const choice = getElements(this.els['option-choice'].selector, {
        context: el,
      }).find(el => el.checked)?.value

      return {
        label: name,
        value: choice,
      }
    })

    return options
  }

  onVariantChange (event) {
    const variant = this.getVariant()
    const multipack = this.closest(selectors.multipack) !== null

    if (!variant) {
      this.resetPage()
      return
    }

    // variant.updateCustomSoldOutText(event); // interesting
    variant.updateSwatchDescription(event)
    this.updateVariant(variant, event, multipack)
  }
}

export class QuickBuyVariantSelects extends VariantOptions {
  constructor () {
    super()
    this.setupRadioListeners()
    this.setupSelectListeners()
  }

  setupRadioListeners () {
    const radioElements = getElements('[data-quick-buy-variant-radios-el]')
    radioElements.forEach(el => {
      el.addEventListener('change', this.handleRadioChange)
    })
  }

  handleRadioChange (event) {
  }

  setupSelectListeners () {
    const selectElements = getElements('[data-quick-buy-variant-selects-el]')
    selectElements.forEach(el => {
      el.addEventListener('change', this.handleSelectChange)
    })
  }

  handleSelectChange (event) {
    const selectedValue = event.target.value
    const radioElement = document.querySelector(`[data-quick-buy-variant-radios-el][value="${selectedValue}"]`)

    if (radioElement && !radioElement.checked) {
      radioElement.checked = true
      radioElement.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  get selectedOptions () {
    return this.els.option.elements.map(el => {
      const { name } = el.dataset

      return {
        label: name,
        value: el.querySelector('select').value,
      }
    })
  }

  onVariantChange (event) {
    const variant = this.getVariant()
    const multipack = this.closest(selectors.multipack) !== null

    if (!variant) {
      this.resetPage()
      return
    }

    this.updateVariant(variant, event, multipack)
  }
}

if (!customElements.get('quick-buy-variant-radios')) {
  customElements.define('quick-buy-variant-radios', QuickBuyVariantRadios)
}

if (!customElements.get('quick-buy-variant-selects')) {
  customElements.define('quick-buy-variant-selects', QuickBuyVariantSelects)
}
